import React, { useState, useMemo, useEffect } from 'react';
import {
  Overlay, TopBar, Title, VersionSelect, VsLabel, CloseBtn, Body, VarsRow, ExampleControlsRow,
  VarGroup, VarLabel, VarInput, RunButton, Spinner, ResultsGrid, ResultColumn, ColumnHeader, Badge,
  ResultBox, LoadingBox, ErrorBox, EmptyBox, DiffLauncherRow, DiffLauncherBtn, DiffPanel,
  DiffPanelHeader, AccordionBody, DiffBlock, DiffLine, FieldLabel, EditToggle, EditPanesGrid,
  EditPane, EditLabel, EditTextarea, SaveVersionBtn, SmallSelect, SmallButton, MetaInfo,
} from './OutputComparisonModal.styles';
import { diffLines } from 'diff';
import { X, Play, ChevronDown, ChevronRight, Cpu, Edit3, Lock, Save, Check } from 'lucide-react';
import API_ENDPOINTS from '../../config/api';



function extractVariables(text) {
  if (!text) return [];
  const matches = text.match(/\{\{(\w+)\}\}/g) || [];
  return [...new Set(matches.map(m => m.replace(/[{}]/g, '')))].filter(v => v !== 'if' && v !== '/if');
}

function composeSystemPrompt(technicalPrompt, qualitativePrompt, fallbackPrompt = '') {
  const parts = [];
  const tech = String(technicalPrompt || '').trim();
  const qual = String(qualitativePrompt || '').trim();
  if (tech) parts.push(`# Core Rules\n\n${tech}`);
  if (qual) parts.push(`# Domain Guidance\n\n${qual}`);
  if (parts.length > 1) {
    parts.push('# Precedence\n\nIf Core Rules and Domain Guidance conflict, Core Rules take priority.');
  }
  const combined = parts.join('\n\n');
  return combined || fallbackPrompt || '';
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('fi-FI') + ' ' + d.toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' });
}

function DiffSection({ oldText, newText }) {
  const parts = useMemo(() => diffLines(oldText || '', newText || ''), [oldText, newText]);
  if (!oldText && !newText) return null;
  return (
    <DiffBlock>
      {parts.map((part, i) => {
        const lines = part.value.split('\n').filter((l, idx, arr) => idx < arr.length - 1 || l);
        return lines.map((line, j) => (
          <DiffLine
            key={`${i}-${j}`}
            $type={part.added ? 'added' : part.removed ? 'removed' : 'context'}
          >
            {part.added ? '+ ' : part.removed ? '- ' : '  '}{line}
          </DiffLine>
        ));
      })}
    </DiffBlock>
  );
}

const OutputComparisonModal = ({
  promptKey,
  promptName,
  versions,
  currentTemplate,
  instanceId,
  onClose,
  onRefreshVersions,
}) => {
  const [versionA, setVersionA] = useState('');
  const [versionB, setVersionB] = useState('current');
  const [varValues, setVarValues] = useState({});
  const [resultA, setResultA] = useState(null);
  const [resultB, setResultB] = useState(null);
  const [loadingA, setLoadingA] = useState(false);
  const [loadingB, setLoadingB] = useState(false);
  const [errorA, setErrorA] = useState(null);
  const [errorB, setErrorB] = useState(null);
  const [activeDiffPanel, setActiveDiffPanel] = useState(null);

  const [editMode, setEditMode] = useState(false);
  const [editA, setEditA] = useState({ systemPromptTechnical: '', systemPromptQualitative: '', userPromptTemplate: '' });
  const [editB, setEditB] = useState({ systemPromptTechnical: '', systemPromptQualitative: '', userPromptTemplate: '' });
  const [saveLoadingA, setSaveLoadingA] = useState(false);
  const [saveLoadingB, setSaveLoadingB] = useState(false);
  const [saveSuccessA, setSaveSuccessA] = useState(false);
  const [saveSuccessB, setSaveSuccessB] = useState(false);
  const [instances, setInstances] = useState([]);
  const [selectedInstanceId, setSelectedInstanceId] = useState(instanceId || '');
  const [loadingInstances, setLoadingInstances] = useState(false);
  const [loadingExample, setLoadingExample] = useState(false);
  const [exampleMeta, setExampleMeta] = useState(null);

  useEffect(() => {
    setSelectedInstanceId(instanceId || '');
  }, [instanceId]);

  useEffect(() => {
    let cancelled = false;
    const loadInstances = async () => {
      setLoadingInstances(true);
      try {
        const res = await fetch(API_ENDPOINTS.INSTANCES());
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setInstances(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) setInstances([]);
      } finally {
        if (!cancelled) setLoadingInstances(false);
      }
    };
    loadInstances();
    return () => { cancelled = true; };
  }, []);

  const getPromptText = (ver) => {
    if (ver === 'current') {
      const technical = currentTemplate?.system_prompt_technical ?? currentTemplate?.system_prompt ?? '';
      const qualitative = currentTemplate?.system_prompt_qualitative || '';
      return {
        systemPromptTechnical: technical,
        systemPromptQualitative: qualitative,
        systemPrompt: composeSystemPrompt(technical, qualitative, currentTemplate?.system_prompt || ''),
        userPromptTemplate: currentTemplate?.user_prompt_template || '',
        model: currentTemplate?.model || 'sonnet',
        maxTokens: currentTemplate?.max_tokens || 1000,
        temperature: currentTemplate?.temperature ?? 1.0,
      };
    }
    const snap = versions?.find(v => String(v.version) === String(ver));
    const technical = snap?.system_prompt_technical ?? snap?.system_prompt ?? '';
    const qualitative = snap?.system_prompt_qualitative || '';
    return {
      systemPromptTechnical: technical,
      systemPromptQualitative: qualitative,
      systemPrompt: composeSystemPrompt(technical, qualitative, snap?.system_prompt || ''),
      userPromptTemplate: snap?.user_prompt_template || '',
      model: snap?.model || 'sonnet',
      maxTokens: snap?.max_tokens || 1000,
      temperature: snap?.temperature ?? 1.0,
    };
  };

  const populateEdits = (verA, verB) => {
    if (verA) setEditA(getPromptText(verA));
    if (verB) setEditB(getPromptText(verB));
  };

  const handleToggleEdit = () => {
    const next = !editMode;
    setEditMode(next);
    if (next) populateEdits(versionA, versionB);
    setSaveSuccessA(false);
    setSaveSuccessB(false);
  };

  const handleVersionAChange = (val) => {
    setVersionA(val);
    if (editMode && val) setEditA(getPromptText(val));
    setSaveSuccessA(false);
  };

  const handleVersionBChange = (val) => {
    setVersionB(val);
    if (editMode && val) setEditB(getPromptText(val));
    setSaveSuccessB(false);
  };

  const allVariables = useMemo(() => {
    const texts = [
      composeSystemPrompt(
        currentTemplate?.system_prompt_technical ?? currentTemplate?.system_prompt ?? '',
        currentTemplate?.system_prompt_qualitative || '',
        currentTemplate?.system_prompt || ''
      ),
      currentTemplate?.user_prompt_template,
      ...(versions || []).flatMap(v => [
        composeSystemPrompt(v.system_prompt_technical ?? v.system_prompt ?? '', v.system_prompt_qualitative || '', v.system_prompt || ''),
        v.user_prompt_template,
      ]),
    ].filter(Boolean).join(' ');
    return extractVariables(texts);
  }, [currentTemplate, versions]);

  const canRun = versionA && versionB && versionA !== versionB;

  const runTestVersion = async (version, setResult, setLoading, setError) => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await fetch(API_ENDPOINTS.ADMIN_PROMPT_TEST_VERSION(promptKey), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ version: version === 'current' ? 'current' : parseInt(version, 10), variables: varValues, instanceId: selectedInstanceId || null }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Request failed' }));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      setResult(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const runTestAdhoc = async (editData, setResult, setLoading, setError) => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await fetch(API_ENDPOINTS.ADMIN_PROMPT_TEST_ADHOC(promptKey), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          systemPrompt: composeSystemPrompt(
            editData.systemPromptTechnical || '',
            editData.systemPromptQualitative || '',
            ''
          ) || null,
          systemPromptTechnical: editData.systemPromptTechnical || '',
          systemPromptQualitative: editData.systemPromptQualitative || '',
          userPromptTemplate: editData.userPromptTemplate,
          model: editData.model,
          maxTokens: editData.maxTokens,
          temperature: editData.temperature,
          variables: varValues,
          instanceId: selectedInstanceId || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Request failed' }));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      setResult(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRun = async () => {
    if (!canRun) return;
    // Avoid bursting two parallel Anthropic calls (often triggers 529 Overloaded)
    if (editMode) {
      await runTestAdhoc(editA, setResultA, setLoadingA, setErrorA);
      await runTestAdhoc(editB, setResultB, setLoadingB, setErrorB);
    } else {
      await runTestVersion(versionA, setResultA, setLoadingA, setErrorA);
      await runTestVersion(versionB, setResultB, setLoadingB, setErrorB);
    }
  };

  const handleSave = async (editData, setSaveLoading, setSaveSuccess) => {
    setSaveLoading(true);
    try {
      // Global save; the picked instance is test data only (#689).
      const res = await fetch(API_ENDPOINTS.ADMIN_PROMPT(promptKey), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          systemPrompt: composeSystemPrompt(
            editData.systemPromptTechnical || '',
            editData.systemPromptQualitative || '',
            ''
          ) || null,
          systemPromptTechnical: editData.systemPromptTechnical || '',
          systemPromptQualitative: editData.systemPromptQualitative || '',
          userPromptTemplate: editData.userPromptTemplate,
          model: editData.model,
          maxTokens: editData.maxTokens,
          temperature: editData.temperature,
          changeNote: 'Hot edit from comparison tool',
        }),
      });
      if (!res.ok) throw new Error('Save failed');
      setSaveSuccess(true);
      if (onRefreshVersions) onRefreshVersions();
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleLoadExampleData = async () => {
    if (!promptKey || !selectedInstanceId || loadingExample) return;
    setLoadingExample(true);
    setErrorA(null);
    setErrorB(null);
    try {
      const res = await fetch(API_ENDPOINTS.ADMIN_PROMPT_EXAMPLE_DATA(promptKey, {
        instanceId: selectedInstanceId,
        includeRag: true,
      }), { credentials: 'include' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Request failed' }));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setVarValues(prev => ({ ...prev, ...(data.variables || {}) }));
      setExampleMeta(data.meta || null);
    } catch (err) {
      setErrorA(err.message);
    } finally {
      setLoadingExample(false);
    }
  };

  const snapA = useMemo(() => versions?.find(v => String(v.version) === String(versionA)), [versions, versionA]);
  const snapB = useMemo(() => versions?.find(v => String(v.version) === String(versionB)), [versions, versionB]);

  const getVersionLabel = (ver) => {
    if (ver === 'current') {
      const namePart = currentTemplate?.version_name ? ` — ${currentTemplate.version_name}` : '';
      return `Nykyinen (v${currentTemplate?.version})${namePart}`;
    }
    const snap = versions?.find(v => String(v.version) === String(ver));
    if (!snap) return `v${ver}`;
    const namePart = snap.version_name ? ` — ${snap.version_name}` : '';
    return `v${snap.version}${namePart} — ${formatDate(snap.created_at)}`;
  };

  const getDiffTextA = (field) => {
    if (editMode) {
      if (field === 'system_prompt_technical') return editA.systemPromptTechnical;
      if (field === 'system_prompt_qualitative') return editA.systemPromptQualitative;
      if (field === 'system_prompt') return composeSystemPrompt(editA.systemPromptTechnical, editA.systemPromptQualitative, '');
      return editA.userPromptTemplate;
    }
    if (field === 'system_prompt_technical') return snapA?.system_prompt_technical ?? snapA?.system_prompt ?? '';
    if (field === 'system_prompt_qualitative') return snapA?.system_prompt_qualitative ?? '';
    return snapA?.[field] || '';
  };
  const getDiffTextB = (field) => {
    if (editMode) {
      if (field === 'system_prompt_technical') return editB.systemPromptTechnical;
      if (field === 'system_prompt_qualitative') return editB.systemPromptQualitative;
      if (field === 'system_prompt') return composeSystemPrompt(editB.systemPromptTechnical, editB.systemPromptQualitative, '');
      return editB.userPromptTemplate;
    }
    if (versionB === 'current') {
      if (field === 'system_prompt_technical') return currentTemplate?.system_prompt_technical ?? currentTemplate?.system_prompt ?? '';
      if (field === 'system_prompt_qualitative') return currentTemplate?.system_prompt_qualitative ?? '';
      return currentTemplate?.[field] || '';
    }
    if (field === 'system_prompt_technical') return snapB?.system_prompt_technical ?? snapB?.system_prompt ?? '';
    if (field === 'system_prompt_qualitative') return snapB?.system_prompt_qualitative ?? '';
    return snapB?.[field] || '';
  };

  const editADirty = editMode && versionA && (
    editA.systemPromptTechnical !== getPromptText(versionA).systemPromptTechnical ||
    editA.systemPromptQualitative !== getPromptText(versionA).systemPromptQualitative ||
    editA.userPromptTemplate !== getPromptText(versionA).userPromptTemplate
  );
  const editBDirty = editMode && versionB && (
    editB.systemPromptTechnical !== getPromptText(versionB).systemPromptTechnical ||
    editB.systemPromptQualitative !== getPromptText(versionB).systemPromptQualitative ||
    editB.userPromptTemplate !== getPromptText(versionB).userPromptTemplate
  );

  return (
    <Overlay onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <TopBar>
        <Title>Vertaa: {promptName || promptKey}</Title>
        <VersionSelect value={versionA} onChange={e => handleVersionAChange(e.target.value)}>
          <option value="">Versio A...</option>
          {(versions || []).map(v => (
            <option key={v.version} value={v.version}>
              {`v${v.version}${v.version_name ? ` — ${v.version_name}` : ''} — ${formatDate(v.created_at)}`}
            </option>
          ))}
        </VersionSelect>
        <VsLabel>vs</VsLabel>
        <VersionSelect value={versionB} onChange={e => handleVersionBChange(e.target.value)}>
          <option value="">Versio B...</option>
          <option value="current">
            {`Nykyinen (v${currentTemplate?.version})${currentTemplate?.version_name ? ` — ${currentTemplate.version_name}` : ''}`}
          </option>
          {(versions || []).map(v => (
            <option key={v.version} value={v.version}>
              {`v${v.version}${v.version_name ? ` — ${v.version_name}` : ''} — ${formatDate(v.created_at)}`}
            </option>
          ))}
        </VersionSelect>
        <EditToggle $active={editMode} onClick={handleToggleEdit}>
          {editMode ? <Edit3 size={13} /> : <Lock size={13} />}
          {editMode ? 'Muokkaa' : 'Lukittu'}
        </EditToggle>
        <CloseBtn onClick={onClose}><X size={18} /></CloseBtn>
      </TopBar>

      <Body>
        {editMode && versionA && versionB && (
          <EditPanesGrid>
            <EditPane>
              <EditLabel>A — Tekninen prompt</EditLabel>
              <EditTextarea
                value={editA.systemPromptTechnical}
                onChange={e => setEditA(prev => ({ ...prev, systemPromptTechnical: e.target.value }))}
                $minHeight="80px"
              />
              <EditLabel>A — Sisältöprompt</EditLabel>
              <EditTextarea
                value={editA.systemPromptQualitative}
                onChange={e => setEditA(prev => ({ ...prev, systemPromptQualitative: e.target.value }))}
                $minHeight="80px"
              />
              <EditLabel>A — Tehtäväprompt</EditLabel>
              <EditTextarea
                value={editA.userPromptTemplate}
                onChange={e => setEditA(prev => ({ ...prev, userPromptTemplate: e.target.value }))}
                $minHeight="120px"
              />
            </EditPane>
            <EditPane>
              <EditLabel>B — Tekninen prompt</EditLabel>
              <EditTextarea
                value={editB.systemPromptTechnical}
                onChange={e => setEditB(prev => ({ ...prev, systemPromptTechnical: e.target.value }))}
                $minHeight="80px"
              />
              <EditLabel>B — Sisältöprompt</EditLabel>
              <EditTextarea
                value={editB.systemPromptQualitative}
                onChange={e => setEditB(prev => ({ ...prev, systemPromptQualitative: e.target.value }))}
                $minHeight="80px"
              />
              <EditLabel>B — Tehtäväprompt</EditLabel>
              <EditTextarea
                value={editB.userPromptTemplate}
                onChange={e => setEditB(prev => ({ ...prev, userPromptTemplate: e.target.value }))}
                $minHeight="120px"
              />
            </EditPane>
          </EditPanesGrid>
        )}

        <ExampleControlsRow>
          <SmallSelect
            value={selectedInstanceId}
            onChange={(e) => setSelectedInstanceId(e.target.value)}
            disabled={loadingInstances}
          >
            <option value="">{loadingInstances ? 'Ladataan instansseja...' : 'Testidata: valitse instanssi'}</option>
            {instances.map(inst => (
              <option key={inst.id} value={inst.id}>
                {inst.title || inst.id}
              </option>
            ))}
          </SmallSelect>
          <SmallButton onClick={handleLoadExampleData} disabled={!selectedInstanceId || loadingExample}>
            {loadingExample ? <Spinner size={13} /> : <Cpu size={13} />}
            {loadingExample ? 'Ladataan...' : 'Lataa esimerkkidata'}
          </SmallButton>
        </ExampleControlsRow>

        {allVariables.length > 0 && (
          <VarsRow>
            {allVariables.map(v => (
              <VarGroup key={v}>
                <VarLabel>{'{{' + v + '}}'}</VarLabel>
                <VarInput
                  value={varValues[v] || ''}
                  onChange={e => setVarValues(prev => ({ ...prev, [v]: e.target.value }))}
                  placeholder={`Syötä ${v}...`}
                />
              </VarGroup>
            ))}
          </VarsRow>
        )}
        {exampleMeta && (
          <MetaInfo>
            Lähde: {exampleMeta.instanceTitle || exampleMeta.instanceId || '-'}
            {exampleMeta.sampleScenarioTitle ? ` · skenaario: ${exampleMeta.sampleScenarioTitle}` : ''}
            {exampleMeta.sampleNodeId ? ` · solmu: ${exampleMeta.sampleNodeId}` : ''}
            {exampleMeta.sampleSolutionId ? ` · ratkaisu: ${exampleMeta.sampleSolutionId}` : ''}
          </MetaInfo>
        )}

        <RunButton disabled={!canRun || loadingA || loadingB} onClick={handleRun}>
          {(loadingA || loadingB) ? <Spinner size={16} /> : <Play size={16} />}
          {(loadingA || loadingB) ? 'Ajetaan...' : 'Aja vertailu'}
        </RunButton>

        <ResultsGrid>
          <ResultColumn>
            <ColumnHeader>
              {getVersionLabel(versionA) || 'Versio A'}
              {editADirty && <Badge $bg="rgba(245, 158, 11, 0.15)" $color="#f59e0b">muokattu</Badge>}
              {resultA?.inlineStyleApplied && <Badge $bg="rgba(99, 102, 241, 0.15)" $color="#a5b4fc">suomen tyyli</Badge>}
              {resultA?.model && <Badge>{resultA.model.split('-').slice(-2).join('-')}</Badge>}
              {resultA?.usage && (
                <Badge $bg="rgba(34, 197, 94, 0.1)" $color="#4ade80">
                  <Cpu size={10} style={{ display: 'inline', verticalAlign: '-1px', marginRight: 4 }} />
                  {resultA.usage.input_tokens + resultA.usage.output_tokens}
                </Badge>
              )}
            </ColumnHeader>
            {loadingA ? (
              <LoadingBox><Spinner size={16} /> Ajetaan versiota A...</LoadingBox>
            ) : errorA ? (
              <ErrorBox>{errorA}</ErrorBox>
            ) : resultA ? (
              <>
                <ResultBox>{resultA.response}</ResultBox>
              </>
            ) : (
              <EmptyBox>Aja vertailu nähdäksesi tulokset</EmptyBox>
            )}
            {editMode && editADirty && resultA && (
              <SaveVersionBtn
                disabled={saveLoadingA || saveSuccessA}
                $success={saveSuccessA}
                onClick={() => handleSave(editA, setSaveLoadingA, setSaveSuccessA)}
              >
                {saveSuccessA ? <><Check size={13} /> Tallennettu</> : saveLoadingA ? <><Spinner size={13} /> Tallennetaan...</> : <><Save size={13} /> Tallenna uutena versiona</>}
              </SaveVersionBtn>
            )}
          </ResultColumn>

          <ResultColumn>
            <ColumnHeader>
              {getVersionLabel(versionB) || 'Versio B'}
              {editBDirty && <Badge $bg="rgba(245, 158, 11, 0.15)" $color="#f59e0b">muokattu</Badge>}
              {resultB?.inlineStyleApplied && <Badge $bg="rgba(99, 102, 241, 0.15)" $color="#a5b4fc">suomen tyyli</Badge>}
              {resultB?.model && <Badge>{resultB.model.split('-').slice(-2).join('-')}</Badge>}
              {resultB?.usage && (
                <Badge $bg="rgba(34, 197, 94, 0.1)" $color="#4ade80">
                  <Cpu size={10} style={{ display: 'inline', verticalAlign: '-1px', marginRight: 4 }} />
                  {resultB.usage.input_tokens + resultB.usage.output_tokens}
                </Badge>
              )}
            </ColumnHeader>
            {loadingB ? (
              <LoadingBox><Spinner size={16} /> Ajetaan versiota B...</LoadingBox>
            ) : errorB ? (
              <ErrorBox>{errorB}</ErrorBox>
            ) : resultB ? (
              <>
                <ResultBox>{resultB.response}</ResultBox>
              </>
            ) : (
              <EmptyBox>Aja vertailu nähdäksesi tulokset</EmptyBox>
            )}
            {editMode && editBDirty && resultB && (
              <SaveVersionBtn
                disabled={saveLoadingB || saveSuccessB}
                $success={saveSuccessB}
                onClick={() => handleSave(editB, setSaveLoadingB, setSaveSuccessB)}
              >
                {saveSuccessB ? <><Check size={13} /> Tallennettu</> : saveLoadingB ? <><Spinner size={13} /> Tallennetaan...</> : <><Save size={13} /> Tallenna uutena versiona</>}
              </SaveVersionBtn>
            )}
          </ResultColumn>
        </ResultsGrid>

        {(versionA && versionB && versionA !== versionB) && (
          <DiffLauncherRow>
            <DiffLauncherBtn onClick={() => setActiveDiffPanel('prompt')}>
              <ChevronRight size={13} /> Prompt-diff
            </DiffLauncherBtn>
          </DiffLauncherRow>
        )}

        {activeDiffPanel === 'prompt' && (
          <DiffPanel>
            <DiffPanelHeader>
              <span>Prompt-muutokset (teksti-diff)</span>
              <CloseBtn onClick={() => setActiveDiffPanel(null)}><X size={16} /></CloseBtn>
            </DiffPanelHeader>
            <AccordionBody>
              <FieldLabel>Tekninen prompt</FieldLabel>
              <DiffSection oldText={getDiffTextA('system_prompt_technical')} newText={getDiffTextB('system_prompt_technical')} />
              <FieldLabel>Sisältöprompt</FieldLabel>
              <DiffSection oldText={getDiffTextA('system_prompt_qualitative')} newText={getDiffTextB('system_prompt_qualitative')} />
              <FieldLabel>Tehtäväprompt</FieldLabel>
              <DiffSection oldText={getDiffTextA('user_prompt_template')} newText={getDiffTextB('user_prompt_template')} />
            </AccordionBody>
          </DiffPanel>
        )}

      </Body>
    </Overlay>
  );
};

export default OutputComparisonModal;
